import { computed, observable } from 'mobx';
import type { DepartmentResponseArgs } from './department/department2';
import Department, { convertDepartmentResponseToArgs } from './department/department2';
import type { ChargingDepartmentShareResponseArgs } from './department/charging_department_share2';
import ChargingDepartmentShare, {
  convertChargingDepartmentShareResponseToArgs
} from './department/charging_department_share2';
import type { ProjectResponseArgs } from './project/project2';
import Project, { convertProjectResponseToArgs } from './project/project2';
import type { ProjectShareResponseArgs } from './project/project_share2';
import ProjectShare, { convertProjectShareResponseToArgs } from './project/project_share2';
import ExpensesAccountType, {
  convertExpensesAccountTypeResponseToArgs,
  type ExpensesAccountTypeResponseArgs
} from './expenses/expenses_account_type2';

export type OrderItemMappingMode = 'item' | 'traveler' | 'both' | 'default';

export type OrderItemMappingResponseArgs = {
  id: number;
  traveler_information_id: number;
  charging_department_id?: number | null;
  charging_department?: DepartmentResponseArgs;
  charging_department_share_id?: number | null;
  charging_department_share?: ChargingDepartmentShareResponseArgs;
  display_charging_department?: string | null;
  display_project?: string | null;
  project_id?: number | null;
  project?: ProjectResponseArgs;
  project_share_id?: number | null;
  project_share?: ProjectShareResponseArgs;
  expenses_account_type_id?: number;
  expenses_account_type?: ExpensesAccountTypeResponseArgs;
  traveler_id?: number;
  order_item_id?: number;
  order_item_type?: string;
  order_item_index?: number;
};

export type OrderItemMappingArgs = {
  id?: number;
  travelerInformationId?: number;
  chargingDepartmentId?: number | null;
  chargingDepartment?: Department;
  chargingDepartmentShareId?: number | null;
  chargingDepartmentShare?: ChargingDepartmentShare;
  displayChargingDepartment?: string | null;
  displayProject?: string | null;
  projectId?: number | null;
  project?: Project;
  projectShareId?: number | null;
  projectShare?: ProjectShare;
  expensesAccountTypeId?: number;
  expensesAccountType?: ExpensesAccountType;
  travelerId?: number;
  orderItemId?: number;
  orderItemType?: string;
  orderItemIndex?: number;
  selectedOptionLabel?: string;
};

export const convertOrderItemMappingResponseToArgs = (response: OrderItemMappingResponseArgs) => {
  const args: OrderItemMappingArgs = {
    id: response.id,
    travelerInformationId: response.traveler_information_id,
    chargingDepartmentId: response.charging_department_id,
    chargingDepartment: response.charging_department
      ? new Department(convertDepartmentResponseToArgs(response.charging_department))
      : undefined,
    chargingDepartmentShareId: response.charging_department_share_id,
    chargingDepartmentShare: response.charging_department_share
      ? new ChargingDepartmentShare(
          convertChargingDepartmentShareResponseToArgs(response.charging_department_share)
        )
      : undefined,
    displayChargingDepartment: response.display_charging_department,
    displayProject: response.display_project,
    projectId: response.project_id,
    project: response.project ? new Project(convertProjectResponseToArgs(response.project)) : undefined,
    projectShareId: response.project_share_id,
    projectShare: response.project_share
      ? new ProjectShare(convertProjectShareResponseToArgs(response.project_share))
      : undefined,
    expensesAccountTypeId: response.expenses_account_type_id,
    expensesAccountType: response.expenses_account_type
      ? new ExpensesAccountType(convertExpensesAccountTypeResponseToArgs(response.expenses_account_type))
      : undefined,
    travelerId: response.traveler_id,
    orderItemId: response.order_item_id,
    orderItemType: response.order_item_type,
    orderItemIndex: response.order_item_index
  };
  return args;
};

export default class OrderItemMapping {
  @observable id?: number;
  @observable travelerInformationId?: number;
  @observable chargingDepartmentId?: number | null;
  @observable chargingDepartment?: Department;
  @observable chargingDepartmentShareId?: number | null;
  @observable chargingDepartmentShare?: ChargingDepartmentShare;
  @observable displayChargingDepartment?: string | null;
  @observable displayProject?: string | null;
  @observable projectId?: number | null;
  @observable project?: Project;
  @observable projectShareId?: number | null;
  @observable projectShare?: ProjectShare;
  @observable expensesAccountTypeId?: number;
  @observable expensesAccountType?: ExpensesAccountType;
  @observable travelerId?: number;
  @observable orderItemId?: number;
  @observable orderItemType?: string;
  @observable orderItemIndex?: number;
  @observable selectedOptionLabel?: string;

  constructor(args: OrderItemMappingArgs) {
    this.id = args.id;
    this.travelerInformationId = args.travelerInformationId;
    this.chargingDepartmentId = args.chargingDepartmentId;
    this.chargingDepartment = args.chargingDepartment;
    this.chargingDepartmentShareId = args.chargingDepartmentShareId;
    this.chargingDepartmentShare = args.chargingDepartmentShare;
    this.displayChargingDepartment = args.displayChargingDepartment;
    this.displayProject = args.displayProject;
    this.projectId = args.projectId;
    this.project = args.project;
    this.projectShareId = args.projectShareId;
    this.projectShare = args.projectShare;
    this.expensesAccountTypeId = args.expensesAccountTypeId;
    this.expensesAccountType = args.expensesAccountType;
    this.travelerId = args.travelerId;
    this.orderItemId = args.orderItemId;
    this.orderItemType = args.orderItemType;
    this.orderItemIndex = args.orderItemIndex;
    this.selectedOptionLabel = args.selectedOptionLabel;
  }

  @computed get isChargingDepartmentExist() {
    return !!this.chargingDepartmentId;
  }

  @computed get isProjectExist() {
    return !!this.projectId || !!this.projectShareId;
  }

  @computed get isExpensesAccountTypeExist() {
    return !!this.expensesAccountTypeId;
  }

  @computed get orderItemMappingType(): OrderItemMappingMode | '' {
    let type: OrderItemMappingMode | '' = '';
    if (
      this.travelerId !== null &&
      this.travelerId !== undefined &&
      this.orderItemIndex !== null &&
      this.orderItemIndex !== undefined
    ) {
      if (this.travelerId >= 0 && this.orderItemIndex >= 0) {
        type = 'both';
      } else if (this.travelerId < 0 && this.orderItemIndex >= 0) {
        type = 'item';
      } else if (this.travelerId >= 0 && this.orderItemIndex < 0) {
        type = 'traveler';
      } else {
        type = 'default';
      }
    }
    return type;
  }

  @computed get isForDetail(): boolean {
    const mode = this.orderItemMappingType;
    if (mode) {
      return !['', 'default'].includes(mode);
    }
    return false;
  }

  @computed get submitParams() {
    return {
      id: this.id,
      traveler_information_id: this.travelerInformationId,
      traveler_id: this.travelerId,
      order_item_id: this.orderItemId,
      order_item_type: this.orderItemType,
      order_item_index: this.orderItemIndex,
      project_id: this.projectId || null,
      project_share_id: this.projectShareId || null,
      charging_department_id: this.chargingDepartmentId || null,
      expenses_account_type_id: this.expensesAccountTypeId || null,
      charging_department_share_id: this.chargingDepartmentShareId || null
    };
  }
}
